import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import {
  useGetTutorialPreferencesQuery,
  useResetTutorialCuesMutation,
  useSaveTutorialCueMutation,
  useUpdateTutorialPreferencesMutation,
} from "_shared/tutorials/_api/tutorial-preferences-api";
import { createTutorialDriver, type TutorialDriver } from "_shared/tutorials/tutorial-driver";
import { getEligibleTutorialCues } from "_shared/tutorials/tutorial-preference-state";
import { getTutorialCues, ONBOARDING_PROPOSAL_TUTORIAL_ID } from "_shared/tutorials/tutorial-registry";
import {
  TutorialButton,
  TutorialButtonGroup,
  TutorialControlBar,
  TutorialIconButton,
  TutorialTipIcon,
} from "_shared/tutorials/tutorial-styled-components";
import type { TutorialCue, TutorialProviderValue, TutorialTargetId } from "_shared/tutorials/tutorial-types";
import { useSession } from "users/__session/session-context";

const TutorialContext = createContext<TutorialProviderValue | undefined>(undefined);

export function TutorialProvider({ children }: { readonly children: React.ReactNode }) {
  const session = useSession();
  const theme = useTheme();
  const { pathname } = useLocation();
  const [targetsVersion, setTargetsVersion] = useState(0);
  const [localDisabled, setLocalDisabled] = useState(false);
  const [localSuppressed, setLocalSuppressed] = useState<Set<string>>(() => new Set());
  const [activeCue, setActiveCue] = useState<TutorialCue | null>(null);
  const [pendingTutorialId, setPendingTutorialId] = useState<string | null>(null);
  const [restartTutorialId, setRestartTutorialId] = useState<string | null>(null);
  const targetsRef = useRef(new Map<string, HTMLElement>());
  const driverRef = useRef<TutorialDriver | null>(null);
  const focusBeforeRunRef = useRef<HTMLElement | null>(null);
  const suppressDestroyRef = useRef(false);
  const automaticRunRef = useRef(false);
  const cueSaveQueueRef = useRef<Promise<void>>(Promise.resolve());

  const preferencesQuery = useGetTutorialPreferencesQuery(
    { session },
    { skip: !session?.isLoggedIn },
  );
  const [updatePreferences] = useUpdateTutorialPreferencesMutation();
  const [saveCue] = useSaveTutorialCueMutation();
  const [resetCues] = useResetTutorialCuesMutation();

  const preferences = preferencesQuery.data;
  const tutorialsEnabled = !localDisabled && (preferences?.enabled ?? true);

  const reportPersistenceFailure = useCallback((_action: string, _error: unknown) => {
    enqueueSnackbar("Could not save tutorial preferences. Please try again.", { variant: "error" });
  }, []);

  const registerTarget = useCallback((targetId: TutorialTargetId, element: HTMLElement | null) => {
    if (element) targetsRef.current.set(targetId, element);
    else targetsRef.current.delete(targetId);
    setTargetsVersion((version) => version + 1);
  }, []);

  const persistCue = useCallback((cue: TutorialCue, state: "Dismissed" | "Completed") => {
    const key = `${cue.tutorialId}:${cue.cueId}`;
    setLocalSuppressed((current) => new Set(current).add(key));
    cueSaveQueueRef.current = cueSaveQueueRef.current
      .then(async () => {
        await saveCue({
          session,
          cue: {
            tutorialId: cue.tutorialId,
            cueId: cue.cueId,
            contentVersion: cue.contentVersion,
            state,
          },
        }).unwrap();
      })
      .catch((error: unknown) => reportPersistenceFailure("save", error));
  }, [reportPersistenceFailure, saveCue, session]);

  const stopDriver = useCallback(() => {
    suppressDestroyRef.current = true;
    driverRef.current?.destroy();
    driverRef.current = null;
    setActiveCue(null);
    suppressDestroyRef.current = false;
    const previousFocus = focusBeforeRunRef.current;
    focusBeforeRunRef.current = null;
    previousFocus?.focus();
  }, []);

  const disableTutorials = useCallback(() => {
    setLocalDisabled(true);
    void updatePreferences({ session, enabled: false }).unwrap().catch((error: unknown) => {
      setLocalDisabled(false);
      reportPersistenceFailure("disable", error);
    });
    stopDriver();
  }, [reportPersistenceFailure, session, stopDriver, updatePreferences]);

  const startTutorial = useCallback((tutorialId?: string, ignorePersistedState = false) => {
    if (!tutorialsEnabled || activeCue) return;
    const available = getEligibleTutorialCues({
      cues: getTutorialCues(tutorialId),
      preferences: ignorePersistedState ? [] : (preferences?.cues ?? []),
      locallySuppressed: localSuppressed,
      targets: targetsRef.current,
      pathname,
    });
    if (available.length === 0) {
      if (tutorialId) setPendingTutorialId(tutorialId);
      return;
    }
    setPendingTutorialId(null);
    const guide = tutorialId ? available : [available[0]];
    focusBeforeRunRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    automaticRunRef.current = !tutorialId;
    setActiveCue(guide[0]);
    const driverInstance = createTutorialDriver({
      steps: guide,
      targets: targetsRef.current,
      reducedMotion: globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false,
      theme,
      onDisableTutorials: disableTutorials,
      onDestroyed: () => {
        if (!suppressDestroyRef.current && automaticRunRef.current && guide[0]) {
          persistCue(guide[0], "Dismissed");
        }
        if (!suppressDestroyRef.current && !automaticRunRef.current) {
          guide.forEach((cue) => persistCue(cue, "Completed"));
        }
        driverRef.current = null;
        setActiveCue(null);
        const previousFocus = focusBeforeRunRef.current;
        focusBeforeRunRef.current = null;
        previousFocus?.focus();
      },
    });
    driverRef.current = driverInstance;
    driverInstance.drive();
  }, [activeCue, disableTutorials, localSuppressed, pathname, persistCue, preferences?.cues, theme, tutorialsEnabled]);

  const dismissCue = useCallback((cue: TutorialCue) => {
    persistCue(cue, "Dismissed");
    stopDriver();
  }, [persistCue, stopDriver]);

  const enableTutorials = useCallback(() => {
    setLocalDisabled(false);
    void updatePreferences({ session, enabled: true }).unwrap().catch((error: unknown) => {
      setLocalDisabled(true);
      reportPersistenceFailure("enable", error);
    });
  }, [reportPersistenceFailure, session, updatePreferences]);

  const persistReset = useCallback(async (): Promise<boolean> => {
    const disabledBeforeReset = localDisabled;
    const suppressedBeforeReset = localSuppressed;
    setLocalDisabled(false);
    setLocalSuppressed(new Set());
    try {
      await resetCues({ session }).unwrap();
      return true;
    } catch (error) {
      setLocalDisabled(disabledBeforeReset);
      setLocalSuppressed(suppressedBeforeReset);
      reportPersistenceFailure("reset", error);
      return false;
    }
  }, [localDisabled, localSuppressed, reportPersistenceFailure, resetCues, session]);

  const resetTutorials = useCallback(() => persistReset(), [persistReset]);

  const restartTutorials = useCallback(() => {
    void persistReset().then((reset) => {
      if (reset) setRestartTutorialId(ONBOARDING_PROPOSAL_TUTORIAL_ID);
    });
  }, [persistReset]);

  useEffect(() => {
    if (!restartTutorialId || activeCue) return;
    const tutorialId = restartTutorialId;
    const timer = globalThis.setTimeout(() => {
      setRestartTutorialId(null);
      startTutorial(tutorialId, true);
    }, 0);
    return () => globalThis.clearTimeout(timer);
  }, [activeCue, restartTutorialId, startTutorial]);

  useEffect(() => {
    if (!pendingTutorialId || activeCue) return;
    const timer = globalThis.setTimeout(() => startTutorial(pendingTutorialId), 0);
    return () => globalThis.clearTimeout(timer);
  }, [activeCue, pendingTutorialId, startTutorial, targetsVersion]);

  useEffect(() => {
    if (activeCue || !tutorialsEnabled || preferencesQuery.isLoading) return;
    const timer = globalThis.setTimeout(() => startTutorial(), 150);
    return () => globalThis.clearTimeout(timer);
  }, [activeCue, preferencesQuery.isLoading, startTutorial, targetsVersion, tutorialsEnabled]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && activeCue) stopDriver();
    };
    globalThis.addEventListener("keydown", handleEscape);
    return () => globalThis.removeEventListener("keydown", handleEscape);
  }, [activeCue, stopDriver]);

  const value = useMemo<TutorialProviderValue>(() => ({
    startTutorial,
    dismissCue,
    disableTutorials,
    enableTutorials,
    resetTutorials,
    restartTutorials,
    tutorialsEnabled,
    isRunning: Boolean(activeCue),
    registerTarget,
  }), [activeCue, disableTutorials, dismissCue, enableTutorials, registerTarget, resetTutorials, restartTutorials, startTutorial, tutorialsEnabled]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
      {activeCue && (
        <TutorialControlBar
          aria-label="Tutorial controls"
          elevation={4}
          role="toolbar"
        >
          <TutorialButtonGroup>
            <TutorialTipIcon />
            <TutorialButton onClick={() => dismissCue(activeCue)} size="small" variant="text">
              Dismiss this tip
            </TutorialButton>
            <TutorialIconButton aria-label="Close tutorial controls" onClick={stopDriver} size="small">
              <CloseIcon />
            </TutorialIconButton>
          </TutorialButtonGroup>
        </TutorialControlBar>
      )}
    </TutorialContext.Provider>
  );
}

export function useTutorials(): TutorialProviderValue {
  const context = React.useContext(TutorialContext);
  if (!context) throw new Error("useTutorials must be used inside TutorialProvider.");
  return context;
}
